import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  CategoryFormDataType,
  CategoryRelationFormDataType,
  ClassifierFormDataType,
  CloneFormTableRowButton,
  CreateFormTableRowButton,
  defaultCategoryFormData,
  FormField,
  FormInput,
  FormTableDefaultField,
  FormTableSequenceNumberField,
  getDatePeriodString,
  getFormTableFieldName,
  objectWithMeta,
  objectWithRandomUniqueKey,
  RequiredText,
  TanstackFormTable,
  TextDisabled,
  Tooltip,
  useWatchTableRowCountDebounced,
} from '@atlas-frontend/sdk';
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';
import { Flex } from 'antd';
import { t } from 'i18next';
import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { useClassifierFormLabelTranslation } from '../form/useClassifierFormLabelTranslation';
import translations from './CategoryFormTable.translations.json';
import { CategoryFormTableDeleteButton } from './CategoryFormTableDeleteButton';
import { CategoryGroupEntries } from './CategoryGroupEntries';
import { CategoryRelationsFormModal } from './CategoryRelationsFormModal';
import { CategoryValidPeriodPicker } from './input/CategoryValidPeriodPicker';
import { useSyncHierarchy } from './useSyncHierarchy';

const { addTranslations, useTranslation } = i18nNsFactory(
  'category-form-table',
);
addTranslations(translations);

const columnHelper = createColumnHelper<CategoryFormDataType>();

export interface CategoryFormTableProps {
  name: 'categories' | 'categoryGroups' | 'combinedGroups';
}

export const CategoryFormTable: FC<CategoryFormTableProps> = (props) => {
  const { name } = props;
  const { t: tLabel } = useClassifierFormLabelTranslation();
  const { t } = useTranslation();
  const { getValues } = useFormContext<ClassifierFormDataType>();

  const [modalRowIndex, setModalRowIndex] = useState<number | null>(null);

  const classifierValidPeriod = useWatch<ClassifierFormDataType>({
    name: 'validPeriod',
  }) as [string, string | null];

  const columns = useMemo(() => {
    const columns: ColumnDef<CategoryFormDataType, any>[] = [
      columnHelper.accessor('sequenceNumber', {
        header: tLabel('categories.sequenceNumber.label'),
        cell: FormTableSequenceNumberField,
        meta: {
          width: 110,
          className: 'sticky-left',
          textAlign: 'right',
        },
        filterFn: 'includesString',
        sortingFn: 'basic',
      }),
      columnHelper.accessor('code', {
        header: () => (
          <RequiredText>{tLabel('categories.code.label')}</RequiredText>
        ),
        cell: CodeCell,
        meta: {
          width: '20ch',
          className: 'sticky-left',
        },
      }),
      columnHelper.accessor('name', {
        header: () => (
          <RequiredText>{tLabel('categories.name.label')}</RequiredText>
        ),
        cell: FormTableDefaultField,
        meta: {
          width: '60%',
          minWidth: 150,
        },
      }),
      columnHelper.accessor('nameEng', {
        header: tLabel('categories.nameEng.label'),
        cell: FormTableDefaultField,
        meta: {
          width: '30ch',
        },
      }),
      columnHelper.accessor('description', {
        header: tLabel('categories.description.label'),
        cell: FormTableDefaultField,
        meta: {
          width: '40%',
          minWidth: 150,
        },
      }),
      columnHelper.accessor('validPeriod', {
        header: tLabel('categories.validPeriod.label'),
        cell: (props) => (
          <ValidPeriodCell
            {...props}
            classifierValidPeriod={classifierValidPeriod}
          />
        ),
        meta: {
          width: 230,
        },
        filterFn: 'validPeriod',
      }),
    ];

    if (name === 'categoryGroups' || name === 'combinedGroups') {
      columns.push(
        columnHelper.accessor('categoryRelations', {
          header: tLabel('categories.categoryRelations.label'),
          cell: (props) => (
            <FormGroupEntries
              name={name}
              formDataIndex={props.row.index}
              onClick={setModalRowIndex}
            />
          ),
          meta: {
            width: 100,
          },
        }),
      );
    }

    columns.push(
      columnHelper.display({
        id: 'operations',
        header: () => (
          <Flex justify="center" gap="small">
            <CreateFormTableRowButton
              defaultRowValues={objectWithMeta(
                objectWithRandomUniqueKey({
                  ...defaultCategoryFormData,
                  validPeriod: [classifierValidPeriod[0] || null, null],
                }),
                {
                  addedInUi: true,
                },
              )}
              autoFocusField="code"
            />
          </Flex>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        cell: memo((props) => (
          <Flex justify="center" gap="small">
            <CloneFormTableRowButton context={props} autoFocusField="code" />
            <CategoryFormTableDeleteButton {...props} />
          </Flex>
        )),
        meta: {
          width: 88,
          className: 'sticky-right',
        },
      }),
    );

    return columns;
  }, [name, classifierValidPeriod, tLabel, t, getValues]);

  return (
    <>
      {name === 'categories' && <KeepHierarchyInSync />}
      <CategoryFormTableWrapper vertical gap="large" align="end">
        <TanstackFormTable
          name={name}
          columns={columns}
          sticky
          virtual
          scrollContainerSelector="#detail-layout-main-content"
        />
      </CategoryFormTableWrapper>
      {modalRowIndex !== null && (
        <CategoryRelationsFormModal
          name={name}
          index={modalRowIndex}
          onClose={() => setModalRowIndex(null)}
          classifierValidPeriod={classifierValidPeriod}
        />
      )}
    </>
  );
};

const CategoryFormTableWrapper = styled(Flex)`
  width: 100%;
`;

export const FormGroupEntries: FC<{
  name: string;
  formDataIndex: number;
  onClick: (modalRowIndex: number) => void;
}> = memo((props) => {
  const { name, formDataIndex, onClick } = props;
  const categoryRelations = useWatch({
    name: `${name}.${formDataIndex}.categoryRelations`,
  }) as CategoryRelationFormDataType[];

  const handleClick = useCallback(() => {
    onClick(formDataIndex);
  }, [onClick, formDataIndex]);

  return (
    <CategoryGroupEntries
      id={`category-group-entries-${formDataIndex}`}
      entries={categoryRelations}
      onClick={handleClick}
    />
  );
});

const CodeCell: FC<CellContext<CategoryFormDataType, any>> = memo((props) => {
  const name = getFormTableFieldName(props);
  return (
    <FormField
      name={name}
      size="small"
      preview={
        !props.row.original.inUse || {
          previewOnly: true,
          render: (value) => (
            <Tooltip title={t('code.disabled-if-in-use')}>
              <TextDisabled>{value}</TextDisabled>
            </Tooltip>
          ),
        }
      }
    >
      <FormInput size="small" name={name} autoFocus />
    </FormField>
  );
});

const ValidPeriodCell: FC<
  CellContext<CategoryFormDataType, any> & {
    classifierValidPeriod: [string, string | null];
  }
> = memo((props) => {
  const name = getFormTableFieldName(props);

  return (
    <FormField
      name={name}
      size="small"
      preview={{
        render: (value) => getDatePeriodString(value?.[0], value?.[1]),
      }}
    >
      <CategoryValidPeriodPicker
        name={name}
        size="small"
        autoFocus
        classifierValidPeriod={props.classifierValidPeriod}
      />
    </FormField>
  );
});

const KeepHierarchyInSync: FC = () => {
  const categoriesCount = useWatchTableRowCountDebounced('categories');
  const syncHierarchy = useSyncHierarchy();

  useEffect(() => {
    if (categoriesCount) {
      syncHierarchy();
    }
  }, [categoriesCount, syncHierarchy]);

  return null;
};
